/*
 * MDJ Infinite Scroll
 * -------------------
 * Auto-loads the next page of collection products as the shopper scrolls,
 * using Shopify's Section Rendering API. Progressive enhancement: if this
 * script fails or JS is off, the theme's normal pagination stays visible.
 *
 * Rendered by snippets/mdj-infinite-scroll.liquid, which is called from
 * snippets/pagination.liquid on collection templates only.
 * To revert: delete the MDJ block at the bottom of snippets/pagination.liquid.
 */
(function () {
  'use strict';

  var SUPPORTED =
    'IntersectionObserver' in window &&
    'fetch' in window &&
    'DOMParser' in window &&
    typeof window.customElements !== 'undefined';

  if (!SUPPORTED) return;

  function productKey(item) {
    var link = item.querySelector('a[href]');
    if (!link) return null;
    return link.getAttribute('href').split('?')[0];
  }

  class MdjInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.autoLoads = 0;
      this.seen = new Set();
      this.failures = 0;
      this.onButtonClick = this.onButtonClick.bind(this);
    }

    connectedCallback() {
      this.grid = document.getElementById('product-grid');
      if (!this.grid) return;

      this.nextUrl = this.dataset.nextUrl || '';
      // Dawn stamps the section id onto the grid itself, so we can reuse it
      // for the Section Rendering API without touching the section file.
      this.sectionId = this.grid.dataset.id;
      this.offset = parseInt(this.dataset.triggerOffset, 10) || 6;
      this.maxAuto = parseInt(this.dataset.maxAuto, 10) || 0; // 0 = unlimited
      this.button = this.querySelector('[data-infinite-button]');
      this.spinner = this.querySelector('[data-infinite-spinner]');
      this.status = this.querySelector('[data-infinite-status]');

      // Hide the theme's pagination only once JS is confirmed working.
      document.documentElement.classList.add('mdj-infinite-active');

      // Seed the de-dupe set with what is already on the page.
      this.currentItems().forEach(
        function (item) {
          var key = productKey(item);
          if (key) this.seen.add(key);
        }.bind(this)
      );

      if (this.button) this.button.addEventListener('click', this.onButtonClick);

      if (!this.nextUrl) {
        this.finish();
        return;
      }

      this.observe();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.button) this.button.removeEventListener('click', this.onButtonClick);
    }

    currentItems() {
      return Array.prototype.slice.call(this.grid.querySelectorAll(':scope > .grid__item'));
    }

    onButtonClick() {
      this.autoLoads = 0;
      this.button.hidden = true;
      this.load();
    }

    /* Two triggers, whichever comes first:
       1. the product sitting `offset` cards from the end of the grid — with 24
          loaded and an offset of 6 that is the 19th card, so the next batch
          starts downloading well before the shopper reaches the bottom;
       2. this loader element itself, which sits below the last card and cannot
          be scrolled past. It catches fast flicks and jump-to-bottom scrolls,
          where an interior card can move from below the viewport to above it
          without ever reporting an intersection. */
    observe() {
      if (this.observer) this.observer.disconnect();

      var items = this.currentItems();
      if (!items.length) return;

      this.observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            this.observer.disconnect();
            this.load();
            return;
          }
        }.bind(this),
        { rootMargin: '600px 0px' }
      );

      this.observer.observe(items[Math.max(0, items.length - this.offset)]);
      this.observer.observe(this);
    }

    setBusy(isBusy) {
      if (this.spinner) this.spinner.hidden = !isBusy;
      this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      if (this.button) this.button.hidden = true;
      this.setBusy(false);
      this.announce(this.dataset.doneText || 'All products loaded.');
    }

    showButton() {
      if (!this.button) return;
      this.button.hidden = false;
    }

    load() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.setBusy(true);

      var url;
      try {
        url = new URL(this.nextUrl, window.location.origin);
      } catch (error) {
        this.loading = false;
        this.setBusy(false);
        this.finish();
        return;
      }
      url.searchParams.set('section_id', this.sectionId);

      fetch(url.toString())
        .then(
          function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          }
        )
        .then(
          function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var newGrid = doc.getElementById('product-grid');
            var nextLoader = doc.querySelector('mdj-infinite-scroll');
            var incoming = newGrid
              ? Array.prototype.slice.call(newGrid.querySelectorAll(':scope > .grid__item'))
              : [];

            var fragment = document.createDocumentFragment();
            var added = 0;

            incoming.forEach(
              function (item) {
                var key = productKey(item);
                if (key) {
                  if (this.seen.has(key)) return;
                  this.seen.add(key);
                }
                // Dawn's scroll-reveal animation is initialised on page load
                // only, so strip it and use our own fade instead.
                item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
                item.removeAttribute('data-cascade');
                item.removeAttribute('style');
                item.classList.add('mdj-infinite-new');
                fragment.appendChild(document.importNode(item, true));
                added++;
              }.bind(this)
            );

            if (added) this.grid.appendChild(fragment);

            this.failures = 0;
            this.nextUrl = nextLoader ? nextLoader.dataset.nextUrl || '' : '';
            this.autoLoads++;
            this.loading = false;
            this.setBusy(false);
            this.announce(this.currentItems().length + ' products shown.');

            if (!added || !this.nextUrl) {
              this.finish();
              return;
            }

            if (this.maxAuto > 0 && this.autoLoads >= this.maxAuto) {
              this.showButton();
            } else {
              this.observe();
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            console.error('[MDJ infinite scroll]', error);
            this.failures++;
            this.loading = false;
            this.setBusy(false);
            // Network hiccup: hand control back to the shopper rather than
            // retrying in a loop. Three strikes and we restore pagination.
            if (this.failures >= 3) {
              document.documentElement.classList.remove('mdj-infinite-active');
              this.finish();
              return;
            }
            this.showButton();
          }.bind(this)
        );
    }
  }

  if (!customElements.get('mdj-infinite-scroll')) {
    customElements.define('mdj-infinite-scroll', MdjInfiniteScroll);
  }
})();
